import type { Course } from "../../../shared/lib/backend.js";
import { lockOf } from "../lib/lock.js";
import styles from "./CourseChoice.module.css";

/**
 * „Womit möchtest du üben?“ – die Auswahl zwischen den Lehrgängen der Klasse.
 *
 * Erscheint NUR, wenn es mehr als einen gibt. Bei genau einem gibt es keine
 * Auswahl; eine Frage mit einer einzigen Antwort ist keine Frage, sondern eine
 * Verzögerung vor dem Üben.
 *
 * Ein Lehrgang, der auf einem anderen aufbaut, ist zu, solange dort noch etwas
 * fehlt. Er steht trotzdem da – blass und nicht anklickbar, mit dem Grund
 * daneben. Ihn wegzulassen wäre bequemer zu bauen und schlechter zu verstehen:
 * das Kind wüsste nicht, dass es weitergeht, und der Sprung käme später ohne
 * Vorwarnung.
 *
 * Die Reihenfolge der Lehrgänge kommt vom Server – sie ist die der Klasse.
 */
export function CourseChoice({
  courses,
  onChoose,
}: {
  courses: readonly Course[];
  onChoose: (slug: string) => void;
}) {
  return (
    <div className={styles.page}>
      <div className={styles.column}>
        <h1 className={styles.title}>Womit möchtest du üben?</h1>
        <p className={styles.text}>Du kannst jederzeit wechseln.</p>

        <div className={styles.grid}>
          {courses.map((course) => {
            const lock = lockOf(course, courses);
            return (
              <button
                key={course.slug}
                type="button"
                className={`${styles.course} ${lock ? styles.locked : ""}`}
                disabled={lock !== null}
                onClick={() => onChoose(course.slug)}
              >
                <span className={styles.courseTitle}>{course.title}</span>
                {lock ? (
                  // Kein „gesperrt“, sondern was zu tun ist. Die Zahl macht aus
                  // einer Wand einen Weg: das Kind sieht, wie weit es noch ist.
                  <span className={styles.courseSubtitle}>
                    Übe zuerst {lock.after} zu Ende – noch{" "}
                    {lock.missing === 1 ? "eine Übung" : `${lock.missing} Übungen`}
                  </span>
                ) : (
                  course.subtitle && (
                    <span className={styles.courseSubtitle}>{course.subtitle}</span>
                  )
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
